import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNotify, useTranslate } from 'react-admin';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import {
  updateUser,
  getUserAccounts,
  setUserAccounts,
  getNotifyStaff,
  setNotifyStaff,
  type UserAccountRef,
} from '../services/userService';
import { useIsSuperUser } from '@/utils/permissions';
import { getAccountsList, AccountOption } from '@/features/manage-groups/services/groupService';
import type { UserFormValues, UserRecord } from '../types/user';

const i18nBase = 'resources.user-management';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface UserFormDialogProps {
  /** Account user being edited. */
  record: UserRecord | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const emptyDefaults: UserFormValues = {
  name: '',
  surname: '',
  email: '',
  phone: '',
  telegramChatId: '',
  canUseContactCenterAi: false,
};

export const UserFormDialog: React.FC<UserFormDialogProps> = ({
  record,
  open,
  onClose,
  onSuccess,
}) => {
  const translate = useTranslate();
  const [allAccounts, setAllAccounts] = React.useState<AccountOption[]>([]);
  const [accountIds, setAccountIds] = React.useState<string[]>([]);
  /** Cuentas del usuario CON sus permisos resueltos; se pintan de solo lectura. */
  const [userAccounts, setUserAccountRefs] = React.useState<UserAccountRef[]>([]);
  const notify = useNotify();
  const isSuperUser = useIsSuperUser();
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<UserFormValues>({
    defaultValues: emptyDefaults,
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  useEffect(() => {
    if (!open) return;
    reset({
      id: record?.id,
      name: record?.name ?? '',
      surname: record?.surname ?? '',
      email: record?.email ?? '',
      phone: record?.phone ?? '',
      telegramChatId: record?.telegramChatId ?? '',
      canUseContactCenterAi: record?.canUseContactCenterAi ?? false,
    });
  }, [open, record, reset]);

  // Personal del contact center de Roommatik. Se carga aparte de la ficha porque no vive
  // en ella: hay que preguntarselo a Keycloak. `null` = todavia no se sabe.
  const [notifyStaff, setNotifyStaffState] = React.useState<boolean | null>(null);
  const [notifyStaffInicial, setNotifyStaffInicial] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    if (!open) return;
    void getAccountsList().then(setAllAccounts);
    if (record?.id) {
      void getUserAccounts(record.id)
        .then((accs) => {
          setAccountIds(accs.map((a) => a.id));
          setUserAccountRefs(accs);
        })
        .catch(() => {
          setAccountIds([]);
          setUserAccountRefs([]);
        });
    } else {
      setAccountIds([]);
      setUserAccountRefs([]);
    }

    // Solo SuperUser: el endpoint tambien lo exige, asi que a Maintenance le devolveria 403
    // y se quedaria con un interruptor que no puede mover.
    if (isSuperUser && record?.id) {
      void getNotifyStaff(record.id)
        .then((v) => {
          setNotifyStaffState(v);
          setNotifyStaffInicial(v);
        })
        // Si no se puede saber, no se ensena: es mejor que ensenar «no» y que alguien lo
        // interprete como que se lo acaban de quitar.
        .catch(() => {
          setNotifyStaffState(null);
          setNotifyStaffInicial(null);
        });
    } else {
      setNotifyStaffState(null);
      setNotifyStaffInicial(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, record?.id, isSuperUser]);

  const onSubmit = async (values: UserFormValues): Promise<void> => {
    if (!record) return;
    setSubmitting(true);
    try {
      await updateUser(record.id, {
        name: values.name,
        surname: values.surname,
        email: values.email,
        phone: values.phone.trim() || null,
        telegramChatId: values.telegramChatId.trim() || null,
        canUseContactCenterAi: values.canUseContactCenterAi,
      });
      if (record?.id) {
        await setUserAccounts(record.id, accountIds);
      }
      // Solo si se ha tocado: cada llamada escribe en el realm compartido con el resto de
      // productos, y no hay por que ir ahi al guardar un cambio de telefono.
      if (record?.id && notifyStaff !== null && notifyStaff !== notifyStaffInicial) {
        await setNotifyStaff(record.id, notifyStaff);
      }
      notify(`${i18nBase}.notifications.updated`, { type: 'success' });
      onSuccess();
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          `${i18nBase}.notifications.updateFailed`,
          translate
        ),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {translate(`${i18nBase}.dialog.userDetails`)}
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.name`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="surname"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.surname`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                rules={{
                  required: translate(`${i18nBase}.validation.emailRequired`),
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: translate(`${i18nBase}.validation.emailInvalid`),
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    type="email"
                    label={translate(`${i18nBase}.fields.email`)}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.phone`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="telegramChatId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate(`${i18nBase}.fields.telegramChatId`)}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                {translate(`${i18nBase}.fields.accounts`)}
              </Typography>
              <FormGroup
                sx={{
                  maxHeight: 180,
                  overflowY: "auto",
                  flexWrap: "nowrap",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                {allAccounts.map((a) => (
                  <FormControlLabel
                    key={a.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={accountIds.includes(a.id)}
                        onChange={(_e, checked) =>
                          setAccountIds((prev) =>
                            checked ? [...prev, a.id] : prev.filter((x) => x !== a.id),
                          )
                        }
                      />
                    }
                    label={a.name}
                  />
                ))}
              </FormGroup>
            </Grid>
            {/*
              Personal del contact center de Roommatik. Es lo unico de esta pantalla que NO
              es un dato de la ficha: es un rol del realm de Keycloak, y por eso se guarda
              con su propia llamada. Vive aqui, y no entre los permisos de la cuenta, porque
              esos los reparte el propio hotel desde su portal — si esto fuera uno de ellos,
              un hotel podria concederse el diario que no debe ver.
            */}
            {isSuperUser && notifyStaff !== null && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={notifyStaff}
                      onChange={(_e, checked) => setNotifyStaffState(checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: 14 }}>
                        {translate(`${i18nBase}.fields.notifyStaff`)}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {translate(`${i18nBase}.fields.notifyStaffHelp`)}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </Grid>
            )}
            {/*
              Los permisos son POR CUENTA desde el modelo de `user_account_scopes`. Aqui se
              ENSENAN, no se editan: esta pantalla escribia la columna global antigua, que ya
              no es la que manda, asi que el mismo usuario aparecia con permisos distintos
              segun donde se le mirara. Se editan en Cuentas -> Usuarios, que es el unico
              sitio que sabe que productos tiene contratados cada cuenta.
            */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                {translate(`${i18nBase}.fields.scopesPerAccount`)}
              </Typography>
              {userAccounts.length === 0 && (
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {translate(`${i18nBase}.help.noAccounts`)}
                </Typography>
              )}
              {userAccounts.map((a) => (
                <Box
                  key={a.id}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', py: 0.5 }}
                >
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, minWidth: 160 }}>
                    {a.accountName}
                  </Typography>
                  {(a.scopes ?? []).map((sc) => (
                    <Chip
                      key={sc}
                      size="small"
                      label={translate(`${i18nBase}.scopes.${sc}`)}
                      color={a.scopesConfigured ? 'info' : 'warning'}
                      variant="outlined"
                      sx={{ fontSize: 10.5, height: 20 }}
                    />
                  ))}
                  {(a.scopes ?? []).length === 0 && (
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                      {translate(`${i18nBase}.help.noScopes`)}
                    </Typography>
                  )}
                </Box>
              ))}
              <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.5 }}>
                {translate(`${i18nBase}.help.scopesEditedInAccount`)}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
          <Box
            sx={{
              display: 'flex',
              gap: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate('ra.action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !isValid}
              sx={{
                textTransform: 'none',
                width: { xs: '100%', sm: 140 },
                flex: { xs: '1 1 auto', sm: '0 0 140px' },
                height: 40,
              }}
            >
              {translate('ra.action.save')}
            </Button>
          </Box>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default UserFormDialog;
